"use client";
import { motion, Variants } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import React from "react";

import { Link } from "@/i18n/routing";

const WhyAlba: React.FC = () => {
  const t = useTranslations("Home.whyAlba");
  const locale = useLocale();
  const isRTL = locale === "ar";

  const containerVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const whyAlbaItems = [
    {
      src: "/assets/images/why-alba-rep.png",
      alt: t("items.item1.alt"),
      label: t("items.item1.label"),
    },
    {
      src: "/assets/images/why-albacars-second.png",
      alt: t("items.item2.alt"),
      label: t("items.item2.label"),
    },
    {
      src: "/assets/images/why-albacars-third.png",
      alt: t("items.item3.alt"),
      label: t("items.item3.label"),
    },
  ];

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false, amount: 0.1 }}
      variants={containerVariants}
      className="items-left mb-6 flex flex-col space-y-7 rounded-3xl bg-white py-8 lg:mb-28 lg:items-center lg:space-y-12 lg:rounded-6xl lg:py-20"
    >
      <motion.div variants={itemVariants} className="px-5 text-left lg:px-0 lg:text-center">
        <h2 className={`mb-4 font-urbanist text-xl lg:text-center lg:text-3xl ${isRTL ? "text-right" : "text-left"}`}>
          {t("titleSmallSize")}
          <span className="mt-1 block text-3xl font-semibold lg:text-5xl"> {t("titleBigSize")}</span>
        </h2>
        <h3
          className={`inter mx-auto max-w-4xl text-left text-sm text-[#292D34CC] lg:text-center ${isRTL ? "text-right" : "text-left"}`}
        >
          <strong>{t("subtitleBold")} </strong>
          {t("subtitle")}
        </h3>
      </motion.div>

      <motion.div
        variants={itemVariants}
        className={`flex flex-col justify-center space-y-3 px-5 lg:w-3/4 lg:flex-row lg:space-y-0 ${isRTL ? "lg:space-x-7 lg:space-x-reverse" : "lg:space-x-7"}`}
      >
        {whyAlbaItems.map((item, index) => (
          <motion.div
            key={index}
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
            className="urbanist flex items-center justify-between rounded-3xl bg-secondary-light px-5 text-xl font-medium text-primary-foreground lg:flex-col lg:justify-center lg:p-10 lg:px-10"
          >
            <div className="lg:order-0 relative order-3 mb-4 h-28 w-40 lg:h-36 lg:w-44 2xl:h-60 2xl:w-80">
              <Image src={item.src} alt={item.alt} fill className="object-contain object-bottom" />
            </div>
            <span className="lg:order-7">
              <h4>{item.label}</h4>
            </span>
          </motion.div>
        ))}
      </motion.div>

      <motion.div variants={itemVariants}>
        <div className="mx-auto px-5 text-left lg:max-w-3xl lg:px-0 lg:text-center">
          <h3 className="inter mx-auto text-left text-sm text-[#292D34CC] lg:max-w-3xl lg:px-0 lg:text-center">
            {t("description1")}{" "}
            <Link href="/contact-us" className="text-left text-[#005DFFCC]">
              {t("linkText1")}
            </Link>{" "}
            {t("description2")}
          </h3>
        </div>
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="mx-5 flex rounded-2xl border border-[#E4E8EC] p-4 lg:mx-0 lg:w-3/4 lg:items-center lg:rounded-full lg:p-8"
      >
        <div>
          <Image alt={t("certImageAlt")} src="/assets/images/certification.svg" width={235} height={228} />
        </div>
        <div className={isRTL ? "pr-2 lg:pr-10" : "pl-2 lg:pl-10"}>
          <h2 className="urbanist mb-4 text-xl font-semibold text-primary-foreground">{t("certTitle")}</h2>
          <p className="inter mx-auto max-w-xl text-sm leading-6 text-[#292D34CC]">
            {t("certDescription")} <br /> {t("certDescription2")}
          </p>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default WhyAlba;
